import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, message, Tabs, AutoComplete, Empty, Spin, Tag, Modal, Switch, InputNumber, Tooltip } from 'antd';
import { CloudDownloadOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { listActionGroups } from 'services/settings/actionGroups';
import { listActionTypes } from 'services/settings/actionTypes';
import { listStages } from 'services/settings/stages';
import createProcess from 'services/processes/createProcess';
import updateProcess from 'services/processes/updateProcess';
import ProcessTimeline from 'components/Processes/ProcessActions/Movimentation';
import SetCommentsProcess from 'components/Processes/ProcessActions/setComments';
import listClients from 'services/client/listClient';
import PROCESS_NATURES from 'utils/processNature';
import { searchProcessDataJud } from 'services/datajud';
import listSchedule from 'services/schedule/listSchedule';
import ScheduleProcess from 'components/Processes/ProcessActions/ScheduleProcess';
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md';
import * as C from 'components/Clients/ClientsActions/EditClient/styles';
import * as S from './styles';
import { FaEdit } from "react-icons/fa";
import DeleteProcess from 'components/Processes/ProcessActions/DeleteProcess';
import AtributeProcess from 'components/Processes/ProcessActions/AtributeProcess';
import { cacheClear } from 'utils/memoryCache';
import { addProcessTag, buildTagCatalogFromProcesses, getCachedTagCatalog, getProcessTagCatalog } from 'services/processes/processTags';
import { getErrorMessage } from 'utils/errorMessage';
import { drawerWidth } from 'theme';
import { enrichProcess, RESULTADO_OPTIONS } from 'services/escavador';
import { getIntegracoes } from 'services/integracoes';
import { getUserCachedData } from 'utils/UserAuthorization';
import { getUserRoleValue, hasGeneralAccess } from 'utils/rolePermissions';
import { formatProcessDateInput, formatStoredProcessDate } from 'utils/processDate';
import { filtrarOpcao } from 'utils/buscaAproximada'

const { Option } = Select;

const TAG_COLORS = [
  '#1677ff', '#52c41a', '#faad14', '#f5222d', '#13c2c2', '#722ed1',
  '#eb2f96', '#389e0d', '#f97316', '#8c8c8c', '#1d4ed8', '#7c3aed'
];

const states = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO'
];

const { TabPane } = Tabs;

function formatarCPF(cpf) {
  if (cpf.length !== 11) throw new Error('CPF inválido');
  return cpf.slice(0, 3) + '.' + cpf.slice(3, 6) + '.' + cpf.slice(6, 9) + '-' + cpf.slice(9);
}

function formatarCNPJ(cnpj) {
  if (cnpj.length !== 14) throw new Error('CNPJ inválido');
  return cnpj.slice(0, 2) + '.' + cnpj.slice(2, 5) + '.' + cnpj.slice(5, 8) + '/' + cnpj.slice(8, 12) + '-' + cnpj.slice(12);
}

function formatarDocumento(documento) {
  const digits = String(documento || '').replace(/\D/g, '');
  if (digits.length === 11) return formatarCPF(digits);
  if (digits.length === 14) return formatarCNPJ(digits);
  throw new Error('Documento inválido');
}

const CreateProcess = ({ 
  isEditing, 
  icon, 
  process, 
  successCallback,
  visible: externalVisible,
  onVisibleChange 
}) => {
  const [form] = Form.useForm();

  const [internalVisible, setInternalVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [actionGroups, setActionGroups] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [stages, setStages] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const prefillDoneRef = useRef(false);

  const selectedActionGroupId = Form.useWatch('ActionGroupId', form);
  const selectedPhase = Form.useWatch('Phase', form);
  const [clientOptions, setClientOptions] = useState([]);
  const [searchingClient, setSearchingClient] = useState(false);
  
  const [searchingDataJud, setSearchingDataJud] = useState(false);
  const [dataJudFound, setDataJudFound] = useState(false);
  const [dataJudData, setDataJudData] = useState(null);
  const [processWithMovimentacoes, setProcessWithMovimentacoes] = useState(process);

  const hasRecentMov = useMemo(() => {
    if (!processWithMovimentacoes?.movimentacoes) return false;
    try {
      const parsed = typeof processWithMovimentacoes.movimentacoes === 'string'
        ? JSON.parse(processWithMovimentacoes.movimentacoes)
        : processWithMovimentacoes.movimentacoes;
      const list = parsed?.movimentos || (Array.isArray(parsed) ? parsed : []);
      if (!list.length) return false;
      const now = Date.now();
      const thresholdMs = 7 * 24 * 60 * 60 * 1000;
      return list.some((m) => {
        const time = new Date(m.dataHora || m.data || m.createdAt).getTime();
        return !isNaN(time) && now - time < thresholdMs && now >= time;
      });
    } catch {
      return false;
    }
  }, [processWithMovimentacoes]);

  const [processSchedules, setProcessSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isModalMode, setIsModalMode] = useState(false);

  // Estados para gerenciamento de etiquetas no cadastro/edição
  const [selectedProcessTags, setSelectedProcessTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [tagCatalog, setTagCatalog] = useState([]);
  const navigate = useNavigate();

  const isControlled = typeof externalVisible === 'boolean';
  const visible = isControlled ? externalVisible : internalVisible;

  useEffect(() => {
    if (visible && process) {
      if (Array.isArray(process.tags) && process.tags.length > 0) {
        setSelectedProcessTags(process.tags.map(t => ({ label: t.label, color: t.color || TAG_COLORS[0] })));
      } else {
        setSelectedProcessTags([]);
      }
    } else if (visible && !isEditing) {
      setSelectedProcessTags([]);
    }
    if (visible) {
      getProcessTagCatalog().then((tags) => setTagCatalog(tags)).catch(() => {});
      const cached = getCachedTagCatalog();
      const fromProcess = buildTagCatalogFromProcesses(process ? [process] : []);
      const merged = [...cached, ...fromProcess].reduce((all, tag) => {
        if (!all.some(item => item.label?.toLowerCase() === tag.label?.toLowerCase())) all.push(tag);
        return all;
      }, []);
      setTagCatalog(merged);
    }
  }, [visible, process, isEditing]);
  
  const setVisible = (value) => {
    if (isControlled) {
      onVisibleChange && onVisibleChange(value);
    } else {
      setInternalVisible(value);
    }
  };

  useEffect(() => {
    if (process) {
      setProcessWithMovimentacoes(process);
    }
  }, [process]);

  const handleMovimentacoesUpdated = (movimentacoesData) => {
    console.log('📋 Movimentações atualizadas:', movimentacoesData);
    setProcessWithMovimentacoes(prev => ({
      ...prev,
      movimentacoes: movimentacoesData
    }));
  };

  const getClientDocument = (client) => {
    if (!client) return '';
    return client.cpf || client.cnpj || client.CPFCNPJ || '';
  };

  const searchClients = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setClientOptions([]);
      return;
    }

    setSearchingClient(true);
    try {
      const response = await listClients(searchText);
      const rawClients = response.data?.clients || [];

      const seenKeys = new Set();
      const clients = rawClients.filter(client => {
        const doc = getClientDocument(client);
        const key = client.id || `${client.nome}-${doc}`;
        if (!key || seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });

      const options = clients.map(client => {
        const document = getClientDocument(client) || 'Documento não informado';
        return {
          value: client.id,
          label: `${client.nome} - ${document}`,
          client,
        };
      });

      setClientOptions(options);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setClientOptions([]);
    } finally {
      setSearchingClient(false);
    }
  };

  const searchDataJudAutomatically = useCallback(async (numeroProcesso) => {
    if (!numeroProcesso || numeroProcesso.length < 10) {
      setDataJudFound(false);
      setDataJudData(null);
      return;
    }

    setSearchingDataJud(true);
    try {
      const response = await searchProcessDataJud(numeroProcesso);
      const data = response?.data || response || null;

      if (data) {
        setDataJudFound(true);
        setDataJudData(data);

        if (data?.NProcesso) {
          form.setFieldsValue(data);
        }
        console.log('📋 Processo encontrado no DataJud');
      } else {
        setDataJudFound(false);
        setDataJudData(null);
      }
    } catch (error) {
      console.log('ℹ️ Não foi possível buscar no DataJud:', error.message);
      setDataJudFound(false);
      setDataJudData(null);
    } finally {
      setSearchingDataJud(false);
    }
  }, [form]);

  const debounceTimerRef = useRef(null);
  const debouncedSearchDataJud = useCallback((numeroProcesso) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      searchDataJudAutomatically(numeroProcesso);
    }, 1500);
  }, [searchDataJudAutomatically]);

  const handleClientSelect = (value, option) => {
    const options = Array.isArray(option) ? option : [option];
    const client = options[0]?.client;
    if (client) {
      const document = getClientDocument(client);
      form.setFieldsValue({
        PartePROPrincipalNome: client.nome,
        PartePROPrincipalCPFCNPJ: document,
        clientSearch: client.nome,
        clientId: client.id,
        clientIds: (Array.isArray(value) ? value : [value]).filter(Boolean)
      });
   
      form.setFields([
        {
          name: 'clientSearch',
          errors: [],
        },
      ]);
    }
  };

  const handleClientClear = () => {
    form.setFieldsValue({
      PartePROPrincipalNome: '',
      PartePROPrincipalCPFCNPJ: '',
      clientSearch: '',
      clientId: undefined,
      clientIds: []
    });
    setClientOptions([]);
  };

  useEffect(() => {
    async function loadConfigData() {
      setConfigLoading(true);
      try {
        const [groupsRes, typesRes, stagesRes] = await Promise.all([
          listActionGroups(),
          listActionTypes(),
          listStages()
        ]);
        setActionGroups(groupsRes.data || groupsRes || []);
        setActionTypes(typesRes.data || typesRes || []);
        setStages(stagesRes.data || stagesRes || []);
      } catch (err) {
        console.error('Erro ao carregar dados de configuração:', err);
      } finally {
        setConfigLoading(false);
      }
    }
    loadConfigData();
  }, []);

  useEffect(() => {
    if (!isEditing || !process) return;
    if (!actionGroups.length || !actionTypes.length || !stages.length) return;
    if (prefillDoneRef.current) return;

    const currentValues = form.getFieldsValue();
    const hasUserSelection = currentValues.ActionGroupId || currentValues.ActionTypeId || currentValues.StageId || currentValues.Phase;

    const norm = (v) => (v || '').toString().trim().toLowerCase();

    const resolvedGroupId = process.ActionGroupId
      || (() => {
        if (!process.ActionGroup) return undefined;
        const g = actionGroups.find(gr => norm(gr.name || gr.nome) === norm(process.ActionGroup));
        return g?.id;
      })();

    const resolvedTypeId = process.ActionTypeId
      || (() => {
        if (!process.ActionTypeName) return undefined;
        const t = actionTypes.find(tp => norm(tp.name || tp.nome) === norm(process.ActionTypeName));
        return t?.id;
      })();

    const resolvedStageId = process.StageId
      || (() => {
        if (!process.Stage) return undefined;
        const s = stages.find(st => norm(st.name || st.nome) === norm(process.Stage));
        return s?.id;
      })();

    const resolvedPhase = process.Phase
      || (() => {
        if (resolvedStageId) {
          const st = stages.find(s => s.id === resolvedStageId);
          return st?.fase;
        }
        return undefined;
      })();

    if (hasUserSelection) {
      if (!currentValues.Phase && resolvedPhase) {
        form.setFieldsValue({ Phase: resolvedPhase });
      }
      prefillDoneRef.current = true;
      return;
    }

    const newValues = {};
    if (resolvedGroupId) newValues.ActionGroupId = resolvedGroupId;
    if (resolvedTypeId) newValues.ActionTypeId = resolvedTypeId;
    if (resolvedStageId) newValues.StageId = resolvedStageId;
    if (resolvedPhase) newValues.Phase = resolvedPhase;

    if (Object.keys(newValues).length) {
      form.setFieldsValue(newValues);
    }
    prefillDoneRef.current = true;
  }, [isEditing, process, actionGroups, actionTypes, stages, form]);

  useEffect(() => {
    if (isEditing && process && actionTypes.length) {
      const typeId = process.ActionTypeId || form.getFieldValue('ActionTypeId');
      const hasGroup = form.getFieldValue('ActionGroupId');
      if (typeId && !hasGroup) {
        const found = actionTypes.find(t => t.id === typeId);
        if (found?.actionGroupId) {
          form.setFieldsValue({ ActionGroupId: found.actionGroupId });
        }
      }
    }
  }, [isEditing, process, actionTypes, form]);

  useEffect(() => {
    if (isEditing && process && stages.length) {
      const stageId = process.StageId || form.getFieldValue('StageId');
      const hasPhase = form.getFieldValue('Phase');
      if (stageId && !hasPhase) {
        const st = stages.find(s => s.id === stageId);
        if (st?.fase) {
          form.setFieldsValue({ Phase: st.fase });
        }
      }
    }
  }, [isEditing, process, stages, form]);

  useEffect(() => {
    if (isEditing && process) {
      if (process.PartePROPrincipalNome) {
        form.setFieldsValue({ clientSearch: process.PartePROPrincipalNome });
      }
      if (process.PartePROPrincipalCPFCNPJ) {
        try {
          form.setFieldsValue({ PartePROPrincipalCPFCNPJ: formatarDocumento(process.PartePROPrincipalCPFCNPJ) });
        } catch (e) {
          form.setFieldsValue({ PartePROPrincipalCPFCNPJ: process.PartePROPrincipalCPFCNPJ });
        }
      }
      const existingClientIds = Array.from(new Set([
        ...(Array.isArray(process.clientIds) ? process.clientIds : []),
        process.clientId
      ].filter(Boolean)));
      if (existingClientIds.length) {
        form.setFieldsValue({
          clientId: process.clientId || existingClientIds[0],
          clientIds: existingClientIds
        });
      }
    }
  }, [isEditing, process, form]);
  
  const loadProcessSchedules = useCallback(async () => {
    if (!process?.NProcesso) {
      setProcessSchedules([]);
      return;
    }
    setLoadingSchedules(true);
    try {
      const { data } = await listSchedule({ processNumber: process.NProcesso });
      setProcessSchedules(data?.schedules || []);
    } catch (error) {
      setProcessSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  }, [process?.NProcesso]);

  useEffect(() => {
    if (activeTab === '4') {
      loadProcessSchedules();
    }
  }, [activeTab, loadProcessSchedules]);

  // "Buscar no tribunal" usa o Escavador (pago): só aparece com a integração ligada
  const [escavadorAtivo, setEscavadorAtivo] = useState(false);
  useEffect(() => { getIntegracoes().then((i) => setEscavadorAtivo(i.escavadorAtivo)); }, []);
  const canFetchTribunal = escavadorAtivo && hasGeneralAccess(getUserRoleValue(getUserCachedData()));
  const [fetchingTribunal, setFetchingTribunal] = useState(false);
  const [resultadoOrigem, setResultadoOrigem] = useState(process?.ResultadoOrigem);
  useEffect(() => { setResultadoOrigem(process?.ResultadoOrigem); }, [process?.ResultadoOrigem]);
  const isResultadoSugerido = ['inferido', 'ia'].includes(resultadoOrigem);

  const handleFetchTribunal = async () => {
    if (!process?.id) return;
    setFetchingTribunal(true);
    try {
      const result = await enrichProcess(process.id);
      if (result?.status === 'atualizado' && result.changes) {
        const { CapaAtualizadaEm, ResultadoOrigem, EscavadorProcessoId, ValorCausaFormatado, ...fields } = result.changes;
        form.setFieldsValue(fields);
        if (ResultadoOrigem) setResultadoOrigem(ResultadoOrigem);
        message.success('Dados do tribunal atualizados. Revise e salve.');
      } else if (result?.status === 'ignorado') {
        message.info(result.motivo || 'Processo sem número CNJ — nada a buscar.');
      } else {
        message.info('Atualização solicitada ao tribunal. Os dados chegam em alguns minutos.');
      }
    } catch (error) {
      message.error(getErrorMessage(error, 'Não foi possível buscar os dados do tribunal.'));
    } finally {
      setFetchingTribunal(false);
    }
  };

  const onFinish = async values => {
    if (saving) return;

    setSaving(true);
    try {
      if (!isEditing && (!values.PartePROPrincipalNome || !values.PartePROPrincipalCPFCNPJ)) {
        message.error('Por favor, selecione um cliente válido!');
        return;
      }

      const payload = { ...values };
      if (isEditing) {
        payload.clientId = payload.clientId || process.clientId || process.clientIds?.[0];
        payload.clientIds = payload.clientIds?.length
          ? payload.clientIds
          : (process.clientIds || (process.clientId ? [process.clientId] : []));
        payload.PartePROPrincipalNome = payload.PartePROPrincipalNome || process.PartePROPrincipalNome;
        payload.PartePROPrincipalCPFCNPJ = payload.PartePROPrincipalCPFCNPJ || process.PartePROPrincipalCPFCNPJ;
      }
      // Salvar com um resultado sugerido = advogado conferiu e confirmou
      if (isResultadoSugerido && payload.ResultadoAcao) payload.confirmarResultado = true;
      if (!payload.clientId && typeof clientOptions?.find === 'function') {
        const opt = clientOptions.find(o => {
          const doc = getClientDocument(o.client);
          return o.client?.nome === payload.PartePROPrincipalNome && doc === payload.PartePROPrincipalCPFCNPJ;
        });
        if (opt?.client?.id) payload.clientId = opt.client.id;
      }

      if (payload.responsaveis && payload.responsaveis.length > 0) {
        const ids = payload.responsaveis.map(r => r.id).join(',');
        const names = payload.responsaveis.map(r => `${r.first_name} ${r.last_name}`).join(', ');
   
        payload.ResponsavelId = ids;
        payload.ResponsavelNome = names;
      }

      const actionGroupObj = actionGroups.find(g => String(g.id) === String(payload.ActionGroupId));
      const actionTypeObj = actionTypes.find(t => String(t.id) === String(payload.ActionTypeId));
      const stageObj = stages.find(s => String(s.id) === String(payload.StageId));

      if (payload.ActionGroupId) {
        payload.ActionGroup = actionGroupObj?.name || actionGroupObj?.nome || '';
      }
      if (payload.ActionTypeId) {
        payload.ActionTypeName = actionTypeObj?.name || actionTypeObj?.nome || '';
      }
      if (payload.StageId) {
        payload.Stage = stageObj?.name || stageObj?.nome || '';
        if (!payload.Phase) {
          payload.Phase = stageObj?.fase || '';
        }
      }
    
      delete payload.ActionGroupId;
      delete payload.StageId;
      delete payload.ActionTypeId;

      if (dataJudData && dataJudData.movimentos && dataJudData.movimentos.length > 0) {
        payload.movimentacoes = JSON.stringify(dataJudData.movimentos);
      }

      let savedProcessId = process?.id;
      if (isEditing) {
        payload.id = process.id;
        delete payload.clientSearch;
        await updateProcess(payload.id, payload);
        cacheClear('processes:');
      } else {
        delete payload.clientSearch;
        const res = await createProcess(payload);
        savedProcessId = res?.id || res?.data?.id || res?.process?.id || res?.data?.process?.id;
      }

      if (savedProcessId && selectedProcessTags.length > 0) {
        try {
          await Promise.all(
            selectedProcessTags.map(tag => addProcessTag(savedProcessId, tag.label, tag.color))
          );
        } catch (tagErr) {
          console.error('Erro ao salvar etiquetas:', tagErr);
        }
        setTagCatalog(getCachedTagCatalog());
      }

      cacheClear('processes:');
      successCallback && typeof successCallback === 'function' && successCallback();
      setVisible(false);
      message.success(isEditing ? 'Processo atualizado com sucesso!' : 'Processo adicionado com sucesso!');
      form.resetFields();
      setClientOptions([]);
      setSelectedProcessTags([]);
    } catch (error) {
      console.error('Erro ao salvar processo:', error);
      const errorData = error.response?.data;
      const errorMessage = typeof errorData === 'string' ? errorData : errorData?.message || errorData?.error;

      if (errorMessage && typeof errorMessage === 'string' &&
          errorMessage.includes("violates foreign key constraint")) {
        return message.error("Verifique se o CPF do processo está associado a algum cliente cadastrado.");
      }

      message.error(getErrorMessage(error, 'Erro ao salvar o processo. Tente novamente.'));
    } finally {
      setSaving(false);
    }
  };

  const renderHeaderTitle = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '24px' }}>
      <span>{isEditing ? 'Editar Processo' : 'Adicionar Processo'}</span>
      <Button 
        type="text" 
        size="small" 
        onClick={() => setIsModalMode(!isModalMode)} 
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
        title={isModalMode ? "Recolher para a Lateral" : "Expandir para Modal"}
      >
        {isModalMode ? <MdFullscreenExit size={26} style={{ color: 'var(--yrm-link)' }} /> : <MdFullscreen size={26} />}
      </Button>
    </div>
  );

  const sharedFooter = isEditing ? (
    <S.FooterEditingContainer>
      <S.ButtonContainer>
        <DeleteProcess processId={process?.id} successCallback={successCallback} />
        <S.StyledButton 
          type="primary"
          loading={saving}
          disabled={saving} 
          className="drawer-primary-btn mobile-update-button"
          onClick={() => form.submit()}
        >
          {saving ? 'Salvando...' : 'Atualizar'}
        </S.StyledButton>
      </S.ButtonContainer>
    </S.FooterEditingContainer>
  ) : (
    <S.FooterContainer>
      <S.StyledButton 
        type="primary" 
        loading={saving}
        disabled={saving}
        className="drawer-primary-btn mobile-add-button"
        onClick={() => form.submit()}
      >
        {saving ? 'Salvando...' : 'Adicionar'}
      </S.StyledButton>
    </S.FooterContainer>
  );

  const sharedContent = (
    <Form form={form} onFinish={onFinish} initialValues={process} layout="vertical">
      <Tabs activeKey={activeTab} onChange={setActiveTab} destroyInactiveTabPane style={{ marginBottom: 24 }}>
        <TabPane tab="Processo" key="1">
          <S.UniformFormWrapper>
              <C.EditClientInputGroup>
                <C.Flex>
                  <C.EditClientInput>
                    <Form.Item 
                      name="NProcesso" 
                      label="Número do Processo"
                      help={
                        searchingDataJud ? (
                          <span style={{ color: 'var(--yrm-link)' }}>🔍 Buscando no DataJud...</span>
                        ) : dataJudFound ? (
                          <span style={{ color: '#52c41a' }}>✅ Dados encontrados no DataJud</span>
                        ) : null
                      }
                    >
                      <Input
                        placeholder="Ex: 0001234-56.2024.8.26.0100"
                        onChange={(e) => debouncedSearchDataJud(e.target.value)}
                      />
                    </Form.Item>
                  </C.EditClientInput>
                  <C.EditClientInput>
                    <Form.Item
                      name="Phase"
                      label="Tipo de Processo"
                      tooltip="Cadastre tanto processos Judiciais quanto Administrativos — os dois são totalmente suportados pelo sistema."
                    >
                      <Select
                        allowClear
                        placeholder="Judicial, Administrativo ou outra categoria"
                        onChange={() => form.setFieldsValue({ StageId: undefined })}
                      >
                        <Option value="Judicial">Judicial</Option>
                        <Option value="Administrativa">Administrativo</Option>
                        <Option value="Marketing">Marketing</Option>
                        <Option value="Negociacao">Negociação</Option>
                        <Option value="Consultoria">Consultoria</Option>
                        <Option value="Recursal">Recursal</Option>
                        <Option value="Rh/financeiro">RH/Financeiro</Option>
                        <Option value="Arquivamento">Arquivamento</Option>
                        <Option value="Execucao/cobranca">Execução/Cobrança</Option>
                      </Select>
                    </Form.Item>
                  </C.EditClientInput>
                  <S.ClientSearchContainer>
                    <C.EditClientInput>
                      <Form.Item 
                        name="clientSearch" 
                        label="Buscar Cliente (Nome ou CPF/CNPJ)"
                        rules={[{ 
                          validator: (_, value) => {
                            const clientName = form.getFieldValue('PartePROPrincipalNome');
                            const clientDoc = form.getFieldValue('PartePROPrincipalCPFCNPJ');
                            
                            if (!clientName || !clientDoc) {
                              return Promise.reject(new Error('Por favor, selecione um cliente válido!'));
                            }
                            return Promise.resolve();
                          }
                        }]}
                        validateTrigger="onSubmit"
                      >
                        <Select
                        mode="multiple"
                          placeholder="Digite o nome ou CPF/CNPJ do cliente"
                          options={clientOptions}
                          onSearch={searchClients}
                          onChange={handleClientSelect}
                          onClear={handleClientClear}
                          loading={searchingClient}
                          filterOption={false}
                          notFoundContent={searchingClient ? 'Buscando...' : 'Nenhum cliente encontrado'}
                          style={{ width: '100%' }}
                          allowClear
                          maxTagCount="responsive"
                        />
                      </Form.Item>
                    </C.EditClientInput>
                  </S.ClientSearchContainer>
                  <C.EditClientInput>
                    <Form.Item name="PartePROPrincipalNome" label="Nome da Parte (Polo Ativo)">
                      <Input disabled placeholder="Preenchido ao selecionar o cliente" />
                    </Form.Item>
                  </C.EditClientInput>
                  <C.EditClientInput>
                    <Form.Item name="PartePROPrincipalCPFCNPJ" label="CPF/CNPJ da Parte (Polo Ativo)">
                      <Input disabled placeholder="Preenchido ao selecionar o cliente" />
                    </Form.Item>
                  </C.EditClientInput>
                </C.Flex>
              </C.EditClientInputGroup>
              
              <C.EditClientInputGroup>
                <C.Flex>
                  <C.EditClientInput>
                    <Form.Item name="ParteCONPrincipalNome" label="Nome da Parte Contrária (Polo Passivo)">
                      <Input placeholder="Nome da parte contrária" />
                    </Form.Item>
                  </C.EditClientInput>
                </C.Flex>
              </C.EditClientInputGroup>

              {/* Seção de Seleção e Criação de Etiquetas */}
              <C.EditClientInputGroup>
                <Form.Item label="Etiquetas do Processo">
                  {selectedProcessTags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {selectedProcessTags.map((tag, idx) => (
                      <Tag
                        key={idx}
                        closable
                        onClose={() => setSelectedProcessTags(prev => prev.filter((_, i) => i !== idx))}
                        style={{
                          margin: 0,
                          borderRadius: 999,
                          padding: '4px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          border: `1px solid ${tag.color}`,
                          backgroundColor: `${tag.color}15`,
                          color: tag.color,
                        }}
                      >
                        {tag.label}
                      </Tag>
                    ))}
                  </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                    <AutoComplete
                      value={newTagInput}
                      onChange={setNewTagInput}
                      options={tagCatalog.map(t => ({ value: t.label }))}
                      placeholder="Nova etiqueta (ex.: Trabalhista)"
                      style={{ flex: 1, minWidth: 0 }}
                    />
                    <Select
                      value={newTagColor}
                      onChange={setNewTagColor}
                      style={{ width: 72 }}
                      aria-label="Cor da etiqueta"
                      options={TAG_COLORS.map(c => ({
                        value: c,
                        label: (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: c, display: 'inline-block' }} />
                          </div>
                        )
                      }))}
                    />
                    <Button
                      type="dashed"
                      onClick={() => {
                        const trimmed = newTagInput.trim();
                        if (!trimmed) return;
                        if (selectedProcessTags.some(t => t.label.toLowerCase() === trimmed.toLowerCase())) return;
                        setSelectedProcessTags(prev => [...prev, { label: trimmed, color: newTagColor }]);
                        setNewTagInput('');
                      }}
                      icon={<PlusOutlined />}
                      aria-label="Adicionar etiqueta"
                      title="Adicionar etiqueta"
                    />
                  </div>
                </Form.Item>
              </C.EditClientInputGroup>

              <C.EditClientInputGroup>
                <C.Flex>
                  <C.EditClientInput>
                    <Form.Item
                      name="DtInicioCausa"
                      label="Data de Início da Causa"
                      getValueProps={(value) => ({ value: value ? (dayjs.isDayjs(value) ? value.format('DD/MM/YYYY') : formatStoredProcessDate(value)) : '' })}
                      normalize={formatProcessDateInput}
                    >
                      <Input
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="DD/MM/AAAA"
                        onChange={(event) => {
                          form.setFieldsValue({ DtInicioCausa: formatProcessDateInput(event.target.value) });
                        }}
                      />
                    </Form.Item>
                  </C.EditClientInput>
                  <C.EditClientInput>
                    <Form.Item name="Natureza" label="Natureza do Processo">
                      <Select allowClear placeholder="Selecione a natureza">
                        {PROCESS_NATURES.map((nature) => (
                          <Option key={nature} value={nature}>{nature}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </C.EditClientInput>
                </C.Flex>
              </C.EditClientInputGroup>

              <C.EditClientInputGroup>
                <C.Flex>
                  <C.EditClientInput>
                    <Form.Item name="ActionGroupId" label="Grupo de ação">
                      <Select 
                        allowClear 
                        showSearch
                        filterOption={filtrarOpcao}
                        placeholder="Selecione o grupo de ação" 
                        loading={configLoading}
                        notFoundContent={configLoading ? <Spin size="small" /> : 'Nenhum grupo cadastrado'}
                        onChange={() => form.setFieldsValue({ ActionTypeId: undefined })}
                      >
                        {actionGroups.map(group => (
                          <Option key={group.id} value={group.id}>{group.name || group.nome}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </C.EditClientInput>
                  <C.EditClientInput>
                    <Form.Item name="ActionTypeId" label="Tipo de ação">
                      <Select
                        allowClear
                        showSearch
                        filterOption={filtrarOpcao}
                        placeholder="Selecione o tipo de ação"
                        loading={configLoading}
                        notFoundContent={configLoading ? <Spin size="small" /> : 'Nenhum tipo cadastrado'}
                      >
                        {actionTypes
                          .filter(t => !selectedActionGroupId || String(t.actionGroupId) === String(selectedActionGroupId))
                          .map(type => (
                            <Option key={type.id} value={type.id}>{type.name || type.nome}</Option>
                          ))}
                      </Select>
                      <Button type="link" size="small" onClick={() => navigate('/settings', { state: { section: 'action-types' } })}>
                      Gerenciar tipos de ação
                      </Button>
                    </Form.Item>
                  </C.EditClientInput>
                  <C.EditClientInput>
                    <Form.Item name="StageId" label="Etapa">
                      <Select 
                        allowClear 
                        showSearch
                        filterOption={filtrarOpcao}
                        placeholder="Selecione a etapa" 
                        loading={configLoading}
                        notFoundContent={configLoading ? <Spin size="small" /> : 'Nenhuma etapa cadastrada'}
                      >
                        {(stages || [])
                          .filter(stage => !selectedPhase || (stage.fase && stage.fase.toLowerCase() === selectedPhase.toLowerCase()))
                          .map(stage => (
                            <Option key={stage.id} value={stage.id}>{stage.name || stage.nome}</Option>
                          ))}
                      </Select>
                      <Button type="link" size="small" onClick={() => navigate('/settings', { state: { section: 'stages' } })}>
                          Gerenciar etapas
                      </Button>
                    </Form.Item>
                  </C.EditClientInput>
                </C.Flex>
              </C.EditClientInputGroup>

              <C.EditClientInput>
                <Form.Item name="responsaveis" label="Responsáveis">
                  <AtributeProcess
                    processId={process?.id}
                    initialUsers={(() => {
                      if (process?.responsaveis && Array.isArray(process.responsaveis) && process.responsaveis.length) return process.responsaveis;
                      if (process?.ResponsavelId || process?.ResponsavelNome) {
                        return { ResponsavelId: process.ResponsavelId || '', ResponsavelNome: process.ResponsavelNome || '' };
                      }
                      return [];
                    })()}
                    successCallback={(data) => {
                      console.log('AtributeProcess callback:', data);
                      const users = data?.users || [];
                      form.setFieldsValue({ 
                        responsaveis: users,
                        ResponsavelNome: data?.ResponsavelNome || '',
                        ResponsavelId: data?.ResponsavelId || ''
                      });
                    }}
                  />
                </Form.Item>
              </C.EditClientInput>

              <C.EditClientInputGroup>
                <C.Flex>
                  <C.EditClientInput>
                    <Form.Item name="Status" label="Status">
                      <Select allowClear placeholder="Selecione o status">
                        <Option value="Acompanhar">Acompanhar</Option>
                        <Option value="Acordo">Acordo</Option>
                        <Option value="Custos">Custos</Option>
                        <Option value="Pago">Pago</Option>
                        <Option value="Pendente">Pendente</Option>
                        <Option value="Manifestação">Manifestação</Option>
                        <Option value="Recurso">Recurso</Option>
                        <Option value="Petição Inicial">Petição Inicial</Option>
                        <Option value="Sem custos">Sem custos</Option>
                      </Select>
                    </Form.Item>
                  </C.EditClientInput>
                  <C.EditClientInput>
                    <Form.Item
                      name="urgente"
                      label="Processo urgente"
                      valuePropName="checked"
                      tooltip="Processos urgentes são consultados no tribunal todos os dias. Os demais, uma vez por semana — o que reduz o custo de monitoramento."
                    >
                      <Switch checkedChildren="Diário" unCheckedChildren="Semanal" />
                    </Form.Item>
                  </C.EditClientInput>
                </C.Flex>
              </C.EditClientInputGroup>

              <C.EditClientInputGroup>
                <C.Flex>
                  <C.EditClientInput>
                    <Form.Item name="MotivodoStatus" label="Motivo do Status">
                      <Input placeholder="Ex: aguardando perícia" />
                    </Form.Item>
                  </C.EditClientInput>
                </C.Flex>
              </C.EditClientInputGroup>

              <C.EditClientInputGroup>
                <C.Flex>
                  <C.EditClientInput>
                    <Form.Item name="UF" label="UF">
                      <Select allowClear showSearch placeholder="UF">
                        {states.map(state => (
                          <Option key={state} value={state}>{state}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </C.EditClientInput>
                  <C.EditClientInput>
                    <Form.Item name="Instancia" label="Instância">
                      <Select allowClear placeholder="Selecione a instância">
                        <Option value="Primeira instância">Primeira instância</Option>
                        <Option value="Segunda instância">Segunda instância</Option>
                      </Select>
                    </Form.Item>
                  </C.EditClientInput>
                </C.Flex>
              </C.EditClientInputGroup>

              <S.SectionTitle>
                Dados do tribunal
                {isEditing && canFetchTribunal && (
                  <Tooltip title="Busca no Escavador o valor da causa, classe, tribunal e fase (custo aprox. R$ 0,05)">
                    <Button
                      size="small"
                      type="link"
                      icon={<CloudDownloadOutlined />}
                      loading={fetchingTribunal}
                      onClick={handleFetchTribunal}
                      style={{ textTransform: 'none', letterSpacing: 0 }}
                    >
                      Buscar no tribunal
                    </Button>
                  </Tooltip>
                )}
              </S.SectionTitle>
              <S.FieldGrid>
                <Form.Item name="Tribunal" label="Tribunal">
                  <Input placeholder="Ex: TJSP, TJRJ, TRT2" />
                </Form.Item>
                <Form.Item name="Classe" label="Classe processual">
                  <Input placeholder="Ex: Procedimento Comum Cível" />
                </Form.Item>
                <Form.Item name="ValorCausa" label="Valor da causa">
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={100}
                    placeholder="0,00"
                    prefix="R$"
                    decimalSeparator=","
                    formatter={(value) => (value === undefined || value === null || value === '' ? '' : String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.'))}
                    parser={(value) => (value ? value.replace(/\./g, '').replace(',', '.') : '')}
                  />
                </Form.Item>
                <Form.Item name="FaseProcessual" label="Fase processual">
                  <Input placeholder="Ex: Em andamento - 1º grau" />
                </Form.Item>
                <Form.Item
                  name="ResultadoAcao"
                  label="Resultado da ação"
                  tooltip="Usado no dashboard (ganhos x perdidos). Quando vier do tribunal, confira e salve para confirmar."
                  extra={isResultadoSugerido ? <Tag color="gold" style={{ marginTop: 4 }}>Sugerido automaticamente — confira</Tag> : null}
                >
                  <Select allowClear placeholder="Selecione o resultado" options={RESULTADO_OPTIONS} />
                </Form.Item>
              </S.FieldGrid>

              <C.EditClientInputGroup>
                <C.Flex>
                  <C.EditClientInput>
                    <Form.Item name="Vara" label="Vara">
                      <Input placeholder="Vara / Órgão Julgador" />
                    </Form.Item>
                  </C.EditClientInput>
                  <C.EditClientInput>
                    <Form.Item name="Foro" label="Foro">
                      <Input placeholder="Foro" />
                    </Form.Item>
                  </C.EditClientInput>
                </C.Flex>
              </C.EditClientInputGroup>

              <C.EditClientInputGroup>
                <C.Flex>
                  <C.EditClientInput>
                    <Form.Item name="Juiz" label="Juiz">
                      <Input placeholder="Nome do Juiz" />
                    </Form.Item>
                  </C.EditClientInput>
                  <C.EditClientInput>
                    <Form.Item name="Assunto" label="Assunto">
                      <Input.TextArea rows={2} placeholder="Assunto(s) do processo" />
                    </Form.Item>
                  </C.EditClientInput>
                </C.Flex>
              </C.EditClientInputGroup>
          </S.UniformFormWrapper>
        </TabPane>
        
        {isEditing && <TabPane
          tab={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Movimentações
              {hasRecentMov && (
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: '#1677ff',
                    display: 'inline-block',
                    boxShadow: '0 0 0 2px rgba(22, 119, 255, 0.25)',
                  }}
                />
              )}
            </span>
          }
          key="2"
        >
          <ProcessTimeline 
            processo={processWithMovimentacoes} 
            onMovimentacoesUpdated={handleMovimentacoesUpdated}
            isActive={activeTab === '2'}
          />
        </TabPane>}
        {isEditing && <TabPane tab="Comentários" key="3">
          <SetCommentsProcess processId={process?.id} initialValue={process?.Comentario ? process.Comentario : JSON.stringify([])} successCallback={successCallback} />
        </TabPane>}
        {isEditing && <TabPane tab="Agendamentos" key="4">
          {!process?.NProcesso ? (
            <Empty description="Salve o processo para vincular agendamentos" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong>Agendamentos vinculados</strong>
              </div>

              {loadingSchedules ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Spin size="small" />
                  Carregando agendamentos...
                </div>
              ) : processSchedules.length === 0 ? (
                <Empty description="Nenhum agendamento para este processo" />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {processSchedules.map(item => (
                    <div
                      key={item.id}
                      style={{
                        padding: 12,
                        border: '1px solid var(--yrm-border)',
                        borderRadius: 10,
                        background: 'var(--yrm-surface)',
                        boxShadow: '0 1px 4px #00000008'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <strong style={{ fontSize: 14 }}>{item.title}</strong>
                          <span style={{ fontSize: 12, color: 'var(--yrm-text-3)' }}>
                            {item.date ? dayjs(item.date).format('DD/MM/YYYY') : '-'}
                          </span>
                        </div>
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                          {item.hour || '00:00'}
                        </Tag>
                      </div>
                      {item.description && (
                        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--yrm-text-2)' }}>
                          {item.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabPane>}
      </Tabs>
    </Form>
  );

  return (
    <>
      <C.DrawerFooterStyles>
        {isModalMode ? (
          <Modal
            title={renderHeaderTitle()}
            open={visible}
            onCancel={() => setVisible(false)}
            footer={sharedFooter}
            width={1000}
            centered
            destroyOnClose
            styles={{
              body: {
                height: 'calc(100vh - 250px)',
                minHeight: '450px',
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingRight: '12px'
              }
            }}
          >
            {sharedContent}
          </Modal>
        ) : (
          <C.EditClientDrawer
            title={renderHeaderTitle()}
            open={visible}
            width={drawerWidth.form}
            mask={false}
            onClose={() => setVisible(false)}
            footer={sharedFooter}
            styles={{
              body: {
                height: 'calc(100vh - 150px)',
                minHeight: '450px',
                overflowY: 'auto'
              }
            }}
          >
            {sharedContent}
          </C.EditClientDrawer>
        )}
      </C.DrawerFooterStyles>

      {isEditing ? (
        <Button type='primary' ghost title='Editar processo' aria-label='Editar processo' icon={icon || <FaEdit />} onClick={() => setVisible(true)} />
      ) : (
        <Button style={{ justifySelf: "flex-end" }} size='large' type='primary' icon={icon || <FaEdit />} onClick={() => setVisible(true)}>
          Novo Processo
        </Button>
      )}

      <ScheduleProcess
        processN={process?.NProcesso || ''}
        open={showScheduleModal}
        onOpenChange={setShowScheduleModal}
        hideButton
        onCreated={loadProcessSchedules}
      />
    </>
  );
};

export default CreateProcess;
